import React, { Component, Fragment } from 'react';
import propTypes from 'prop-types';
import { FormGroup, Input, Label, Popover, PopoverHeader, PopoverBody } from 'reactstrap';

import {
  COUNTRY_CODE,
  CUSTOMER_AUTH_STATE,
  SUPPORTED_CONTENT_CONTEXT,
  VISITOR_STATUS
} from 'app/ContentContext/const';
import { noop } from 'lib/noop';
import { US, International } from 'ui/Icons';

import styles from './RenderPreviewSelection.css';

const FLAG_ICONS = { US, International };

/**
 * inputs for selecting content context preview state
 * @returns {React.Component} preview selection ui
 */
export default class RenderPreviewSelection extends Component {
  static propTypes = {
    selectionStates: propTypes.object,
    updateSelections: propTypes.func
  };

  static defaultProps = {
    selectionStates: {},
    updateSelections: noop
  };

  state = { popoverOpen: false };

  /**
   * handle input changes
   * @param {object} event - change event
   */
  handleChange = event => {
    const { context: key, value } = event?.target?.dataset ?? {};
    this.props.updateSelections({ key, value });
  };

  /**
   * toggle popover open/close state
   * @returns {void}
   */
  togglePopover = () => this.setState(ps => ({ popoverOpen: !ps.popoverOpen }));

  /**
   * render content for popover button based on country selection
   * @returns {string|object[]} string or array of content
   */
  renderButtonContent = () => {
    const RENDER_FOR = 'Render for';
    const selectedCountries = this.props.selectionStates?.[COUNTRY_CODE] ?? [];
    if (!selectedCountries.length) {
      return `${RENDER_FOR} no locations`;
    }

    const allCountries = SUPPORTED_CONTENT_CONTEXT[COUNTRY_CODE];
    if (allCountries.length === selectedCountries.length) {
      return `${RENDER_FOR} all locations`;
    }

    const locations = selectedCountries
      .map(this.renderSmallFlag)
      .flatMap((country, ind) => [ind ? ', ' : ' ', country]);
    return [RENDER_FOR].concat(locations);
  };

  /**
   * render a large flag
   * @param {string} cc - country code (US or International)
   * @returns {Component} small flag img
   */
  renderLargeFlag = cc => (
    <img key={cc} className={styles.largeFlag} alt={cc} src={FLAG_ICONS[cc]} />
  );

  /**
   * render a small flag
   * @param {string} cc - country code (US or International)
   * @returns {Component} small flag img
   */
  renderSmallFlag = cc => (
    <img key={cc} className={styles.smallFlag} alt={cc} src={FLAG_ICONS[cc]} />
  );

  render() {
    const { selectionStates } = this.props;
    const { popoverOpen } = this.state;

    // config to render context inputs
    const renderConfig = {
      [COUNTRY_CODE]: { header: 'Country', renderFlags: true },
      [CUSTOMER_AUTH_STATE]: { header: 'Customer Auth State' },
      [VISITOR_STATUS]: { header: 'Visitor Status' }
    };

    return (
      <Fragment>
        <span
          role="presentation"
          id="countryPopover"
          onClick={this.togglePopover}
          className={styles.wrapper}
          data-test-id="renderSelection-button"
        >
          {this.renderButtonContent()}
        </span>
        <Popover
          isOpen={popoverOpen}
          placement="bottom"
          target="countryPopover"
          toggle={this.togglePopover}
        >
          <span
            className={styles.closeButton}
            onClick={this.togglePopover}
            role="presentation"
            data-test-id="renderSelection-close-button"
          >
            &times;
          </span>
          <PopoverBody className={`p-0 pt-2 ${styles.popoverWrapper}`}>
            {Object.entries(renderConfig).map(([context, { header, renderFlags }]) => (
              <div className="mb-2" key={context}>
                <PopoverHeader>{header}</PopoverHeader>
                <div className={`mt-2 ${styles.contentWrapper}`}>
                  {SUPPORTED_CONTENT_CONTEXT[context].map(value => (
                    <FormGroup key={value} check>
                      <Label check>
                        <Input
                          checked={selectionStates[context].includes(value)}
                          data-context={context}
                          data-value={value}
                          onChange={this.handleChange}
                          type="checkbox"
                          data-test-id={`renderSelection-${context}-${value}`}
                        />
                        &nbsp;
                        {value}
                        {renderFlags && this.renderLargeFlag(value)}
                      </Label>
                    </FormGroup>
                  ))}
                </div>
              </div>
            ))}
          </PopoverBody>
        </Popover>
      </Fragment>
    );
  }
}
